import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePromotionDetail, useUpdatePromotion } from "./api/promotionHooks";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { safeDecodeURIComponent } from "@/lib/utils";

export function EditPromotionPage() {
  const { couponCode: rawCode } = useParams<{ couponCode: string }>();
  const couponCode = safeDecodeURIComponent(rawCode || "");
  const navigate = useNavigate();

  const { data: promotion, isLoading } = usePromotionDetail(couponCode);
  const updateMutation = useUpdatePromotion(couponCode);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [collectionLimit, setCollectionLimit] = useState("");
  const [minCartQty, setMinCartQty] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validUpto, setValidUpto] = useState("");
  const [targetCustomerGroup, setTargetCustomerGroup] = useState("");
  const [error, setError] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Pre-populate form when data loads
  useEffect(() => {
    if (promotion && !initialized) {
      setTitle(promotion.promotion_title || "");
      setDescription(promotion.promotion_description || "");
      setCollectionLimit(
        promotion.max_collections > 0 ? String(promotion.max_collections) : "",
      );
      setMinCartQty(
        promotion.minimum_cart_qty > 0 ? String(promotion.minimum_cart_qty) : "",
      );
      setValidFrom(promotion.valid_from || "");
      setValidUpto(promotion.valid_upto || "");
      setTargetCustomerGroup(promotion.custom_target_customer_group || "");
      setInitialized(true);
    }
  }, [promotion, initialized]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Promotion name is required");
      return;
    }
    if (validFrom && validUpto && validUpto < validFrom) {
      setError("End date must be after start date");
      return;
    }

    updateMutation.mutate(
      {
        promotion_title: title.trim(),
        promotion_description: description.trim(),
        max_collections: collectionLimit ? parseInt(collectionLimit, 10) : 0,
        minimum_cart_qty: minCartQty ? parseInt(minCartQty, 10) : 0,
        valid_from: validFrom || undefined,
        valid_upto: validUpto || undefined,
        custom_target_customer_group: targetCustomerGroup || undefined,
      },
      {
        onError: (err) => {
          setError(err.message || "Failed to update promotion");
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[640px] px-4 pt-12">
        <div className="h-8 w-40 animate-pulse rounded-md bg-gray-100" />
        <div className="mt-8 space-y-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-md bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  if (!promotion) {
    return (
      <div className="mx-auto max-w-[640px] px-4 pt-12">
        <p className="text-muted-foreground">Promotion not found.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/promotions")}
        >
          Back to Promotions
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[640px] px-4 pt-12 pb-16">
      {/* Back link */}
      <button
        onClick={() =>
          navigate(`/promotions/${encodeURIComponent(couponCode)}`)
        }
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Promotion
      </button>

      <h1 className="mb-8 text-2xl font-semibold">Edit Promotion</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Locked fields (read-only) */}
        <section>
          <h2 className="mb-4 text-sm font-semibold text-muted-foreground">
            LOCKED FIELDS
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            These fields cannot be changed after creation to maintain trust with
            customers who already collected this coupon.
          </p>
          <div className="space-y-4">
            <div>
              <Label>Coupon code</Label>
              <Input
                value={promotion.coupon_code}
                disabled
                className="mt-1.5 font-mono bg-gray-50"
              />
            </div>
            <div>
              <Label>Discount</Label>
              <Input
                value={promotion.discount_display}
                disabled
                className="mt-1.5 bg-gray-50"
              />
            </div>
          </div>
        </section>

        {/* Editable fields */}
        <section>
          <h2 className="mb-4 text-sm font-semibold text-muted-foreground">
            EDITABLE FIELDS
          </h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Promotion name *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= 200)
                    setDescription(e.target.value);
                }}
                className="mt-1.5"
                rows={3}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {description.length}/200 characters
              </p>
            </div>
            <div>
              <Label htmlFor="collectionLimit">Collection limit</Label>
              <Input
                id="collectionLimit"
                type="number"
                min="0"
                value={collectionLimit}
                onChange={(e) => setCollectionLimit(e.target.value)}
                className="mt-1.5"
                placeholder="Leave empty for unlimited"
              />
            </div>
            <div>
              <Label htmlFor="minCartQty">Minimum cart quantity</Label>
              <Input
                id="minCartQty"
                type="number"
                min="0"
                value={minCartQty}
                onChange={(e) => setMinCartQty(e.target.value)}
                className="mt-1.5"
                placeholder="e.g., 2"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Minimum number of qualifying items in the cart. Leave empty for no minimum.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="validFrom">Valid from</Label>
                <Input
                  id="validFrom"
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="validUpto">Valid until</Label>
                <Input
                  id="validUpto"
                  type="date"
                  value={validUpto}
                  onChange={(e) => setValidUpto(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Target Customer Group</Label>
              <p className="mb-1.5 text-xs text-muted-foreground">
                Auto-issue this coupon to all customers in the selected group.
              </p>
              <DocTypeSearchAutocomplete
                doctype="Customer Group"
                value={targetCustomerGroup}
                onChange={setTargetCustomerGroup}
                onSelect={setTargetCustomerGroup}
                placeholder="Search customer group..."
                showInitialSuggestions
              />
            </div>
          </div>
        </section>

        {/* Error */}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Footer */}
        <div className="flex items-center justify-between border-t pt-6">
          <button
            type="button"
            onClick={() =>
              navigate(`/promotions/${encodeURIComponent(couponCode)}`)
            }
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
